import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Fetch, API_BASE } from '../../utils/Utils.js';
import DateInput from '../../shared/inputs/DateInput.js';
import '../../herd/ListHerd.css';
import './TryIt.css';

const MessageBubble = ({ type, children }) => (
  <div className={`message-bubble ${type}`}>
    {children}
    <div className="message-time">
      {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
    </div>
  </div>
);

const PhoneMockup = ({ children, chatAreaRef, inputBar, onSettings }) => (
  <div className="phone-mockup">
    <div className="phone-screen">
      <div className="phone-header">
        <div className="phone-header-avatar">ZM</div>
        <div className="phone-header-info">
          <div className="phone-header-name">Zap Manejo</div>
          <div className="phone-header-status">online</div>
        </div>
        {onSettings && (
          <button className="phone-settings-btn" onClick={onSettings} type="button">⚙</button>
        )}
      </div>
      <div className="chat-area" ref={chatAreaRef}>
        {children}
      </div>
      {inputBar && (
        <div className="phone-input-bar">
          {inputBar}
        </div>
      )}
    </div>
    <div className="phone-home-button"></div>
  </div>
);

export const TryIt = () => {
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [message, setMessage] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const chatAreaRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Auto-scroll to bottom when conversation changes
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  // Fetch team members on mount
  useEffect(() => {
    Fetch('/api/data/teams', 'GET', null,
      (data) => {
        setTeamMembers(data || []);
        if (data && data.length > 0) {
          setSelectedPhone(data[0].phone_number);
        }
      },
      () => {
        console.error('Error fetching team members');
      }
    );
  }, []);

  const generateId = (length) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedPhone) return;

    setLoading(true);
    setError('');

    const teamMember = teamMembers.find(m => m.phone_number === selectedPhone);
    const name = teamMember ? teamMember.name : 'Test User';

    const dateObj = selectedDate ? new Date(selectedDate) : new Date();
    const timestamp = Math.floor(dateObj.getTime() / 1000).toString();

    const payload = {
      object: "whatsapp_business_account",
      entry: [{
        id: generateId(20),
        changes: [{
          value: {
            messaging_product: "whatsapp",
            metadata: {
              display_phone_number: "15551939558",
              phone_number_id: "807095752486132"
            },
            contacts: [{
              profile: { name: name },
              wa_id: selectedPhone
            }],
            messages: [{
              from: selectedPhone,
              id: generateId(50),
              timestamp: timestamp,
              text: { body: message },
              type: "text"
            }]
          },
          field: "messages"
        }]
      }]
    };

    const userMessage = { type: 'user', text: message };
    setConversation(prev => [...prev, userMessage]);
    setMessage('');

    try {
      const token = localStorage.getItem('zapmanejo_token');
      const response = await fetch(`${API_BASE}/chat/message?source=dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();

      if (result && result.response_text) {
        setConversation(prev => [...prev, {
          type: 'system',
          text: result.response_text,
          messageType: result.message_type
        }]);
      } else {
        setConversation(prev => [...prev, {
          type: 'system',
          text: t('try_it_no_match'),
          isError: true
        }]);
      }
    } catch (err) {
      setError(t('try_it_error'));
      setConversation(prev => [...prev, {
        type: 'system',
        text: t('try_it_error'),
        isError: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const clearConversation = () => setConversation([]);

  const activeSettingsCount = [selectedPhone, selectedDate].filter(Boolean).length;

  // Mobile: WhatsApp-style input bar inside the phone
  const mobileInputBar = (
    <form className="phone-input-form" onSubmit={handleSubmit}>
      <textarea
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder={t('try_it_placeholder')}
        rows={1}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
          }
        }}
      />
      <button type="submit" disabled={loading || !message.trim()}>
        {loading ? '…' : '➤'}
      </button>
    </form>
  );

  return (
    <div className="try-it">
      <div className="try-it-container">
        <div className="try-it-phone">
          <PhoneMockup
            chatAreaRef={chatAreaRef}
            onSettings={isMobile ? () => setSettingsOpen(true) : undefined}
            inputBar={isMobile ? mobileInputBar : undefined}
          >
            {conversation.length === 0 && (
              <div className="try-it-empty">{t('try_it_empty')}</div>
            )}
            {conversation.map((msg, index) => (
              <MessageBubble key={index} type={msg.type}>
                {msg.text}
                {msg.messageType && (
                  <div className="message-type-badge">{msg.messageType}</div>
                )}
              </MessageBubble>
            ))}
            {loading && (
              <div className="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            )}
          </PhoneMockup>
        </div>

        {!isMobile && (
          <div className="try-it-form">
            <h3>{t('try_it_title')}</h3>
            <p className="try-it-description">{t('try_it_description')}</p>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('phone_number')}</label>
                <select value={selectedPhone} onChange={(e) => setSelectedPhone(e.target.value)}>
                  {teamMembers.map((member) => (
                    <option key={member._id} value={member.phone_number}>
                      {member.name} ({member.phone_number})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('date')}</label>
                <div className="date-input-wrapper">
                  <DateInput
                    value={selectedDate}
                    onChange={(v) => setSelectedDate(v)}
                    required={false}
                    defaultToToday={false}
                    className={selectedDate ? 'has-value' : ''}
                    placeholder={t('select_date')}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('raw_message')}</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('try_it_placeholder')}
                  rows={6}
                />
              </div>

              <div className="form-actions">
                <button type="submit" disabled={loading || !message.trim()}>
                  {loading ? t('sending') : t('send')}
                </button>
                <button type="button" onClick={clearConversation}>
                  {t('clear')}
                </button>
              </div>
            </form>

            {error && <div className="error-message">{error}</div>}
          </div>
        )}
      </div>

      {/* Mobile settings bottom sheet */}
      {settingsOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setSettingsOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t('settings')}</span>
              <button className="filter-sheet-close" onClick={() => setSettingsOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <div className="form-group">
                <label>{t('phone_number')}</label>
                <select value={selectedPhone} onChange={(e) => setSelectedPhone(e.target.value)}>
                  {teamMembers.map((member) => (
                    <option key={member._id} value={member.phone_number}>
                      {member.name} ({member.phone_number})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>{t('date')}</label>
                <DateInput
                  value={selectedDate}
                  onChange={(v) => setSelectedDate(v)}
                  required={false}
                  defaultToToday={false}
                />
              </div>
              <div className="form-group">
                <button type="button" className="try-it-clear-btn" onClick={() => { clearConversation(); setSettingsOpen(false); }}>
                  {t('clear')}
                </button>
              </div>
            </div>
            <div className="filter-sheet-footer">
              <button className="filter-sheet-apply" onClick={() => setSettingsOpen(false)}>
                {t('done')}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TryIt;
